import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FavoriteButton from '@/components/feature/FavoriteButton';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { experienceCategories, verifiedProperties, type PropertyCommunity, type VerifiedProperty } from '@/mocks/properties';
import { propertyRoute } from '@/mocks/propertyRoutes';

type CollectionPageProps = {
  eyebrow: string;
  title: string;
  subtitle: string;
  filter: (property: VerifiedProperty) => boolean;
  progressiveReveal?: boolean;
  sort?: (a: VerifiedProperty, b: VerifiedProperty) => number;
};

type AreaFilter = 'All' | PropertyCommunity;

const areaRoutes: Record<AreaFilter, string> = {
  All: '/vacation-search-results',
  'Panama City Beach': '/pcb-area-search',
  '30A': '/30a-area-search',
  'Rosemary Beach': '/rosemary-beach-search',
  WaterSound: '/watersound',
  Watercolor: '/watercolor-area',
  'Dune Allen': '/dune-allen',
  'Santa Rosa Beach': '/santa-rosa-beach',
};

const areaFilters: AreaFilter[] = [
  'All',
  'Panama City Beach',
  '30A',
  'Rosemary Beach',
  'WaterSound',
  'Dune Allen',
  'Santa Rosa Beach',
];

const amenityRoutes: Record<string, string> = {
  'gulf-front': '/gulf-front-search-results',
  pool: '/pool-search-results',
  'large-groups': '/large-groups-search-results',
  'outdoor-living': '/outdoor-living-search-results',
  'golf-cart': '/golf-cart-search-results',
  'pet-friendly': '/pets-search-results',
};

const chipStyle =
  'bg-[#EFE4D6] text-foreground-700 border-[#DED0BE] hover:border-[#B99F87] hover:text-foreground-950';

function ContinueBrowsing({ variant = 'bottom' }: { variant?: 'top' | 'bottom' }) {
  const availableAreaFilters = areaFilters.filter(
    (area) => area === 'All' || verifiedProperties.some((p) => p.communities.includes(area)),
  );

  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 ${
        variant === 'top' ? 'mb-12 md:mb-16' : 'mt-14 md:mt-20'
      }`}
    >
      <div className="border border-background-300 bg-background-100/70 rounded-lg p-5 md:p-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-700 mb-3">
          Explore by area
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {availableAreaFilters.map((label) => (
            <Link
              key={label}
              to={areaRoutes[label]}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${chipStyle}`}
            >
              {label}
            </Link>
          ))}
        </div>
      </div>

      <div className="border border-background-300 bg-background-100/70 rounded-lg p-5 md:p-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-700 mb-3">
          Explore by amenities
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {experienceCategories.map((category) => (
            <Link
              key={category.id}
              to={amenityRoutes[category.id] ?? '/vacation-search-results'}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${chipStyle}`}
            >
              {category.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function PropertyCard({ property }: { property: VerifiedProperty }) {
  return (
    <article className="relative group">
      <Link
        to={propertyRoute(property.id)}
        aria-label={`View ${property.name}`}
        className="absolute inset-0 z-[1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/70"
      />
      <div className="relative overflow-hidden rounded-2xl bg-background-200 aspect-[4/5]">
        <img
          src={property.image}
          alt={property.name}
          className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/45 via-transparent to-transparent" />
        <FavoriteButton id={property.id} className="absolute top-4 right-4 z-10" />
      </div>
      <div className="pt-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-foreground-500 mb-2">
          {property.location}
        </div>
        <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight">
          {property.name}
        </h2>
        <p className="mt-3 text-foreground-600 text-sm md:text-base font-light leading-relaxed">
          {property.blurb}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {property.highlights.slice(0, 4).map((highlight) => (
            <span
              key={highlight}
              className="text-[11px] uppercase tracking-[0.16em] border border-background-300 text-foreground-700 rounded-lg px-3 py-2"
            >
              {highlight}
            </span>
          ))}
        </div>
        <div className="mt-7 inline-flex items-center gap-2 text-sm bg-foreground-950 text-background-50 px-5 py-2.5 rounded-lg whitespace-nowrap">
          View {property.name}
          <i className="ri-arrow-right-line" />
        </div>
      </div>
    </article>
  );
}

export default function CollectionPage({
  eyebrow,
  title,
  subtitle,
  filter,
  progressiveReveal = false,
  sort,
}: CollectionPageProps) {
  const properties = verifiedProperties.filter(filter).sort(sort);
  const [visibleCount, setVisibleCount] = useState(progressiveReveal ? 6 : Number.POSITIVE_INFINITY);
  const visibleProperties = progressiveReveal ? properties.slice(0, visibleCount) : properties;
  const hiddenCount = Math.max(properties.length - visibleProperties.length, 0);

  useEffect(() => {
    setVisibleCount(progressiveReveal ? 6 : Number.POSITIVE_INFINITY);
  }, [progressiveReveal, title]);

  return (
    <main className="bg-background-50 text-foreground-900 font-body">
      <Header />
      <section className="relative bg-foreground-950 text-background-50 pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 opacity-35">
          <img
            src="https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/f0f81032-e12c-4ae5-9505-de34ac105393_compressed_sea-dreams-45.webp"
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/80 to-foreground-950/45" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.24em] text-accent-300 mb-5">
            <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
            {eyebrow}
          </div>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl leading-[0.98] max-w-4xl">
            {title}
          </h1>
          <p className="mt-6 max-w-2xl text-background-100/82 text-base md:text-lg font-light leading-relaxed">
            {subtitle}
          </p>
          {progressiveReveal && (
            <Link
              to="/map-search"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#EFE4D6] px-5 py-3 text-sm text-foreground-950 transition-colors hover:bg-[#E9DCCB]"
            >
              View map search
              <i className="ri-map-pin-line" />
            </Link>
          )}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-50">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          {progressiveReveal && <ContinueBrowsing variant="top" />}
          {properties.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-10">
                {visibleProperties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
              {hiddenCount > 0 && (
                <div className="mt-12">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + 6)}
                    className="group flex w-full items-center justify-between gap-4 rounded-lg border border-[#DED0BE] bg-[#EFE4D6] px-5 py-4 text-left text-sm text-foreground-950 transition-colors hover:border-[#B99F87] hover:bg-[#E9DCCB] cursor-pointer md:px-7"
                  >
                    <span>
                      <span className="block font-medium">See more properties</span>
                      <span className="mt-1 block text-xs text-foreground-500">
                        {hiddenCount} more in the collection
                      </span>
                    </span>
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-50/70 text-foreground-950 transition-colors group-hover:bg-background-50">
                      <i className="ri-arrow-down-line" />
                    </span>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="max-w-2xl py-8">
              <p className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                More properties are coming to this collection.
              </p>
              <p className="mt-5 text-foreground-600 text-base md:text-lg font-light leading-relaxed">
                Inspire can add the right stays here as the collection grows. For now, explore
                another area or amenity below.
              </p>
              <a
                href="/#concierge"
                className="mt-8 inline-flex items-center gap-2 bg-foreground-950 text-background-50 px-5 py-3 rounded-lg text-sm cursor-pointer hover:bg-accent-600 transition-colors"
              >
                Ask the Insider
                <i className="ri-arrow-right-up-line" />
              </a>
            </div>
          )}
          <ContinueBrowsing />
        </div>
      </section>
      <Footer />
    </main>
  );
}
